/**
 * Service-account access opened from Argo (PE-2215).
 *
 * Argo hands Giotto a one-time ticket; `/accesso-ofc` redeems it for a Parse
 * session of the organization's service account and stores the access here.
 * While the access is present Giotto works in "service-account mode": it never
 * talks to Auth0, authenticates every API call with the Parse session and,
 * when the session ends, sends the operator back to Argo instead of /login.
 *
 * This module only owns the stored access: it has no network or DOM
 * dependency, so the session, routing and API layers can all import it.
 */
export const OFC_ACCESS_STORAGE_KEY = 'ofcAccess';

// Once the access ended in this page, it stays in service-account mode even
// after the storage was cleared: a late API call must not fall back to Auth0.
let endedExitUrl = null;

export const getArgoUrl = () =>
  (process.env.ARGO_URL || '').replace(/\/+$/, '');

// Only an Argo page may be the exit: the value comes from the network and is
// later used as a navigation target.
export const isArgoExitUrl = (url) => {
  const argoUrl = getArgoUrl();
  return typeof url === 'string' && !!argoUrl && url.startsWith(`${argoUrl}/`);
};

/**
 * The stored access, `null` when Giotto is used with Auth0. A malformed value
 * is still an access (an empty one, hence expired): it must never silently
 * downgrade the page to the Auth0 flow with a service-account token stored.
 */
export const getOfcAccess = () => {
  const raw = localStorage.getItem(OFC_ACCESS_STORAGE_KEY);
  if (raw === null || raw === undefined) return null;

  try {
    const access = JSON.parse(raw);
    return access && typeof access === 'object' ? access : {};
  } catch (error) {
    return {};
  }
};

export const isOfcAccess = () =>
  endedExitUrl !== null || getOfcAccess() !== null;

export const isOfcAccessEnded = () => endedExitUrl !== null;

export const isOfcAccessExpired = (now = Date.now()) => {
  if (endedExitUrl !== null) return true;

  const access = getOfcAccess();
  if (!access) return false;

  const expiresAt = Date.parse(access.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= now;
};

export const getOfcExitUrl = () => {
  if (endedExitUrl !== null) return endedExitUrl;

  const exitUrl = getOfcAccess()?.exitUrl;
  return isArgoExitUrl(exitUrl) ? exitUrl : getArgoUrl();
};

export const storeOfcAccess = ({ accessId, exitUrl, expiresAt, operator }) => {
  endedExitUrl = null;
  localStorage.setItem(
    OFC_ACCESS_STORAGE_KEY,
    JSON.stringify({ accessId, exitUrl, expiresAt, operator })
  );
};

// A normal Auth0 login leaves service-account mode for good.
export const clearOfcAccess = () => {
  endedExitUrl = null;
  localStorage.removeItem(OFC_ACCESS_STORAGE_KEY);
};

// Remember where to go back before the storage is wiped.
export const markOfcAccessEnded = () => {
  if (endedExitUrl === null) endedExitUrl = getOfcExitUrl();
  return endedExitUrl;
};
